import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Printer, Edit, Trash2, X, Calendar, Package, User, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { decryptId, encryptId } from '../utils/urlEncryption';
import RevisionTimelineCard from '../components/RevisionTimelineCard';
import PrintFormatSelector from '../components/PrintFormatSelector';
import OrderPrintA4 from '../components/OrderPrintA4';
import OrderPrintThermal from '../components/OrderPrintThermal';
import OrderPrintThermalSummary from '../components/OrderPrintThermalSummary';
import ItemReceiptPrint from '../components/ItemReceiptPrint';
import ItemReceiptSelector from '../components/ItemReceiptSelector';
import { openPrintWindow, getElementOuterHTML } from '../utils/printWindow';
import '../styles/print.css';

const OrderDetailNew = ({ user, onLogout }) => {
  const { orderId: encryptedOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromPage = searchParams.get('from'); // 'pending' or 'provisional'
  
  // Decrypt the order ID from URL
  const orderId = decryptId(encryptedOrderId);
  
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [expandedPaymentMode, setExpandedPaymentMode] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showItemReceiptSelector, setShowItemReceiptSelector] = useState(false);
  const [showItemRate, setShowItemRate] = useState(true);

  useEffect(() => {
    loadOrder();
  }, [orderId, location.key]);

  const loadOrder = async () => {
    try {
      const res = await axiosInstance.get(`/orders/${orderId}`);
      setOrder(res.data);
      
      // Load customer details
      if (res.data.customer_id) {
        const custRes = await axiosInstance.get(`/customers/${res.data.customer_id}`);
        setCustomer(custRes.data);
      }
      
      // Load print settings
      try {
        const settingsRes = await axiosInstance.get('/settings/system');
        if (settingsRes.data?.print_settings) {
          setShowItemRate(settingsRes.data.print_settings.show_item_rate ?? true);
        }
      } catch (e) { /* settings not found is okay */ }
      
      // Load reminder for this order
      try {
        const reminderRes = await axiosInstance.get(`/reminders`, {
          params: { order_id: orderId }
        });
        if (reminderRes.data && reminderRes.data.length > 0) {
          setReminder(reminderRes.data[0]);
        }
      } catch (reminderErr) {
        // Reminder not found is okay
        console.log('No reminder found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading order');
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregate order status based on item statuses
  // Admin approval functions
  const handleApprove = async () => {
    try {
      await axiosInstance.post(`/admin/approve/order/${orderId}`);
      toast.success('Order approved successfully!');
      // Redirect to pending approvals
      setTimeout(() => navigate('/admin/approvals'), 1500);
    } catch (error) {
      console.error('Error approving order:', error);
      toast.error('Failed to approve order');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await axiosInstance.post(`/admin/reject/order/${orderId}?rejection_reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Order rejected successfully');
      setShowRejectModal(false);
      // Redirect to pending approvals
      setTimeout(() => navigate('/admin/approvals'), 1500);
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast.error('Failed to reject order');
    }
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  // Extract Old Gold and Metal Credit items from payments
  const getOldGoldAndMetalCreditItems = () => {
    // Orders use 'advance_payments', Bookings also use 'advance_payments'
    const payments = order?.advance_payments || order?.payments || [];
    
    const items = [];
    
    payments.forEach((payment) => {
      // Old Gold items
      if (payment.mode === 'Old Gold') {
        if (payment.items && Array.isArray(payment.items) && payment.items.length > 0) {
          // Multiple old gold items
          payment.items.forEach((item) => {
            items.push({
              ...item,
              // Map item fields to standardized names
              gross_wt: item.gross_wt || item.grossWt,
              net_wt: item.net_wt || item.netWt,
              less_wt: item.less_wt || item.lessWt,
              fine_wt: item.fine_wt || item.fineWt,
              purity: item.purity,
              rate: item.purchase_rate || item.rate,
              value: item.line_total || item.value,
              description: item.item_name || item.description,
              payment_id: payment.payment_id,
              payment_date: payment.date,
              type: 'old_gold'
            });
          });
        } else {
          // Single old gold payment (top-level fields)
          items.push({
            gross_wt: payment.gross_wt,
            net_wt: payment.net_wt,
            stone_wt: payment.stone_wt,
            less_wt: payment.less_wt,
            purity: payment.purity,
            fine_wt: payment.fine_wt || payment.fine_grams,
            rate: payment.rate_per_gram,
            value: payment.total_value,
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'old_gold'
          });
        }
      }
      
      // Metal Credit items
      if (payment.mode === 'Metal Credit' && payment.deposits && Array.isArray(payment.deposits)) {
        payment.deposits.forEach((deposit) => {
          items.push({
            ...deposit,
            deposits: [deposit], // Wrap single deposit for consistency
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'metal_credit'
          });
        });
      }
    });
    
    return items;
  };

  const handlePrintFormat = (format) => {
    setShowPrintModal(false);
    
    if (format === 'item-receipts') {
      // Show item receipt selector
      setShowItemReceiptSelector(true);
      return;
    }
    
    // Get the HTML content of the selected print format from the proper components
    let content = '';
    let title = '';
    let pageSize = '';
    
    if (format === 'a4') {
      // Get content from the OrderPrintA4 component in the print-formats-container
      const printContainer = document.querySelector('.print-formats-container');
      const a4Component = printContainer?.querySelector('#a4-print-content');
      content = a4Component ? a4Component.outerHTML : '';
      title = `Order ${order?.order_id} - A4 Print`;
      pageSize = 'A4';
    } else if (format === 'thermal') {
      // Get content from the OrderPrintThermal component in the print-formats-container
      const printContainer = document.querySelector('.print-formats-container');
      const thermalComponent = printContainer?.querySelector('#thermal-print-content');
      content = thermalComponent ? thermalComponent.outerHTML : '';
      title = `Order ${order?.order_id} - Thermal Detail`;
      pageSize = 'thermal';
    } else if (format === 'thermal-summary') {
      // Get content from the OrderPrintThermalSummary component in the print-formats-container
      const printContainer = document.querySelector('.print-formats-container');
      const thermalSummaryComponent = printContainer?.querySelector('#thermal-summary-print-content');
      content = thermalSummaryComponent ? thermalSummaryComponent.outerHTML : '';
      title = `Order ${order?.order_id} - Thermal Summary`;
      pageSize = 'thermal';
    }
    
    // Open print window with the selected content
    if (content) {
      openPrintWindow(content, title, { pageSize });
    } else {
      toast.error('Print content not found. Please try again.');
    }
  };

  const handlePrintSelectedItems = (selectedIndices) => {
    const items = getOldGoldAndMetalCreditItems();
    setShowItemReceiptSelector(false);
    
    if (selectedIndices.length === 0) {
      toast.error('Please select at least one receipt to print');
      return;
    }
    
    // Build HTML for selected receipts
    let receiptsHTML = '';
    selectedIndices.forEach((index) => {
      const receiptElement = document.getElementById(`item-receipt-${index}`);
      if (receiptElement) {
        receiptsHTML += receiptElement.outerHTML;
      }
    });
    
    if (!receiptsHTML) {
      toast.error('Selected receipts not found');
      return;
    }
    
    // Open print window with selected receipts
    openPrintWindow(
      receiptsHTML,
      `Order ${order?.order_id} - Item Receipts`,
      { pageSize: 'thermal' }
    );
  };

  const handleEdit = () => {
    navigate(`/orders/edit/${encryptId(orderId)}`);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/orders/${orderId}`);
      toast.success('Order deleted successfully');
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting order');
    }
    setShowDeleteModal(false);
  };

  const calculateTotals = () => {
    // Return comprehensive default object if order or items don't exist
    if (!order || !order.items) {
      return {
        subtotal: 0,
        totalAdvance: 0,
        balance: 0,
        advanceStatusMessage: '',
        advanceStatus: 'none',
        cashAdvance: 0,
        metalAdvanceFineWt: 0,
        metalAdvanceApprox: 0,
        advanceBelowRequired: false,
        totalWeight: 0,
        totalFineWeight: 0,
        totalAdvanceMetal: 0,
        totalMaking: 0,
        totalStone: 0,
        totalAdvanceCash: 0
      };
    }
    
    let subtotal = 0;
    let totalWeight = 0;
    let totalFineWeight = 0;
    let totalMaking = 0;
    let totalStone = 0;
    
    order.items.forEach(item => {
      const weight = item.weight_type === 'Fixed' 
        ? parseFloat(item.weight_fixed || 0)
        : (parseFloat(item.weight_min || 0) + parseFloat(item.weight_max || 0)) / 2;
      let rate = parseFloat(item.rate || 0);
      
      // Apply GST if exclusive
      if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate > 0) {
        rate = rate * 1.03;
      }
      
      const making = parseFloat(item.making_value || 0);
      const stone = parseFloat(item.stone_value || 0);
      
      // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
      let itemTotal;
      let makingAmount = 0;
      
      if (item.making_type === 'WASTAGE') {
        // Wastage/Tonnage: item_total = weight × 24K rate × (tunch / 100)
        let rate24k = parseFloat(order.base24k_rate_ex_gst || 0);
        if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate24k > 0) {
          rate24k = rate24k * 1.03;
        }
        itemTotal = weight * rate24k * (making / 100);
      } else {
        itemTotal = weight * rate;
        if (item.making_type === 'PERCENT') {
          makingAmount = itemTotal * (making / 100);
          itemTotal += makingAmount;
        } else if (item.making_type === 'PER_GRAM') {
          makingAmount = weight * making;
          itemTotal += makingAmount;
        } else if (item.making_type === 'FLAT') {
          makingAmount = making;
          itemTotal += makingAmount;
        }
      }
      
      subtotal += itemTotal;
      totalMaking += makingAmount;
      totalStone += stone;
      
      // Total weight is the entered weight (already total for all pieces)
      totalWeight += weight;
      
      // Calculate fine weight from purity
      let purityPercentage = 0;
      const purity = (item.purity || '').toString().toUpperCase();
      const purityMap = {
        '24K': 100, '24': 100,
        '22K': 91.67, '22': 91.67,
        '18K': 75, '18': 75,
        '14K': 58.5, '14': 58.5,
        '10K': 41.7, '10': 41.7
      };
      purityPercentage = purityMap[purity] || 0;
      
      const fineWeight = weight * (purityPercentage / 100);
      totalFineWeight += fineWeight;
    });
    
    // Calculate cash advance and metal credit separately
    let totalCashAdvance = 0;
    let totalMetalFineWt = 0;
    let hasMetalCredit = false;
    
    order.advance_payments?.forEach(p => {
      if (p.mode === 'Metal Credit') {
        totalMetalFineWt += parseFloat(p.fine_wt || 0);
        hasMetalCredit = true;
      } else {
        const amount = parseFloat(p.amount || 0);
        // Subtract if it's a refund, add if it's a payment
        if (p.transaction_type === 'REFUND') {
          totalCashAdvance -= amount;
        } else {
          totalCashAdvance += amount;
        }
      }
    });
    
    // Calculate approximate cash value of metal credit using order's 24K rate
    let metalAdvanceApprox = 0;
    if (totalMetalFineWt > 0 && order.base24k_rate_ex_gst) {
      metalAdvanceApprox = totalMetalFineWt * order.base24k_rate_ex_gst;
    }
    
    const totalAdvance = totalCashAdvance + metalAdvanceApprox;
    const balance = subtotal - totalAdvance;
    
    // Calculate advance percentage and status message
    let advanceStatusMessage = '';
    let advanceStatus = 'none';
    let advanceBelowRequired = false;
    
    if (subtotal > 0) {
      const advancePercentage = (totalAdvance / subtotal) * 100;
      const requiredPercentage = order.advance_requirement_percentage || 80.0; // Use order-specific threshold, default 80%
      const remainingPercentage = Math.max(0, requiredPercentage - advancePercentage);
      
      if (remainingPercentage > 0) {
        if (hasMetalCredit) {
          advanceStatusMessage = `Adv: ${Math.round(advancePercentage)}% (+ metal), Need: ${Math.round(remainingPercentage)}%`;
        } else {
          advanceStatusMessage = `Adv: ${Math.round(advancePercentage)}%, Need: ${Math.round(remainingPercentage)}%`;
        }
        advanceStatus = 'partial';
        advanceBelowRequired = true;
      } else {
        if (hasMetalCredit) {
          advanceStatusMessage = `Adv: ${Math.round(advancePercentage)}% (+ metal)`;
        } else {
          advanceStatusMessage = `Adv: ${Math.round(advancePercentage)}%`;
        }
        advanceStatus = 'sufficient';
        advanceBelowRequired = false;
      }
    } else if (totalAdvance > 0) {
      advanceStatusMessage = `₹${totalAdvance.toLocaleString('en-IN')}`;
      if (hasMetalCredit) {
        advanceStatusMessage += ' (incl. metal credit)';
      }
      advanceStatus = 'partial';
    }
    
    return { 
      subtotal, 
      totalAdvance,
      cashAdvance: totalCashAdvance,
      metalAdvanceFineWt: totalMetalFineWt,
      metalAdvanceApprox: metalAdvanceApprox, 
      balance, 
      advanceStatusMessage, 
      advanceStatus,
      advanceBelowRequired,
      totalWeight,
      totalFineWeight,
      totalAdvanceMetal: totalMetalFineWt,
      totalMaking,
      totalStone,
      totalAdvanceCash: totalCashAdvance
    };
  };

  const shareOnWhatsApp = () => {
    if (!customer) {
      toast.error('Customer information not available');
      return;
    }
    
    const phone = customer.mobile || customer.phone;
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    // Generate thermal print text format
    const message = generateThermalPrintText();
    
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Generate thermal print text format for WhatsApp
  const generateThermalPrintText = () => {
    const totals = calculateTotals();
    
    // Helper functions
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatIndianCurrency = (amount) => {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString('en-IN', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    };

    const numberToWords = (num) => {
      if (!num || num === 0) return 'Zero Rupees';
      
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      
      const convertLessThanThousand = (n) => {
        if (n === 0) return '';
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
        return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
      };
      
      const absNum = Math.abs(Math.floor(num));
      let result = '';
      
      if (absNum >= 10000000) {
        const crores = Math.floor(absNum / 10000000);
        result += convertLessThanThousand(crores) + ' Crore ';
        num = absNum % 10000000;
      } else {
        num = absNum;
      }
      
      if (num >= 100000) {
        const lakhs = Math.floor(num / 100000);
        result += convertLessThanThousand(lakhs) + ' Lakh ';
        num = num % 100000;
      }
      
      if (num >= 1000) {
        const thousands = Math.floor(num / 1000);
        result += convertLessThanThousand(thousands) + ' Thousand ';
        num = num % 1000;
      }
      
      if (num > 0) {
        result += convertLessThanThousand(num);
      }
      
      return result.trim() + ' Rupees Only';
    };

    const calculateModeSubtotal = (payments) => {
      return payments.reduce((sum, p) => {
        const amount = parseFloat(p.amount) || 0;
        return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
      }, 0);
    };

    // Group payments by mode
    const groupPaymentsByMode = () => {
      const grouped = {};
      (order.advance_payments || []).forEach(payment => {
        const mode = payment.mode || 'Unknown';
        if (!grouped[mode]) {
          grouped[mode] = [];
        }
        grouped[mode].push(payment);
      });
      return grouped;
    };

    const paymentsByMode = groupPaymentsByMode();

    // Build message
    let msg = '';
    
    // Header
    msg += '═══════════════════════════════════\n';
    msg += '         CUSTOMER ORDER\n';
    msg += '═══════════════════════════════════\n';
    msg += `Order ID: ${order.order_id}\n`;
    msg += `Date: ${formatDate(order.order_date || order.created_at)}\n\n`;

    // Customer Information
    msg += '───────────────────────────────────\n';
    msg += 'CUSTOMER INFORMATION\n';
    msg += '───────────────────────────────────\n';
    msg += `Name: ${customer.name}\n`;
    if (customer.mobile) msg += `Mobile: ${customer.mobile}\n`;
    if (customer.alternate_mobile) msg += `Alt Mobile: ${customer.alternate_mobile}\n`;
    if (customer.email) msg += `Email: ${customer.email}\n`;
    if (customer.address) msg += `Address: ${customer.address}\n`;
    msg += '\n';

    // Rate Policy & Base Rate
    if (order.rate_policy || order.base24k_rate_ex_gst) {
      msg += '───────────────────────────────────\n';
      msg += 'RATE INFORMATION\n';
      msg += '───────────────────────────────────\n';
      
      if (order.rate_policy) {
        const policyText = order.rate_policy === 'lock-today' ? 'Rate Booked Today' :
                          order.rate_policy === 'rate-at-delivery' ? 'Rate at Delivery' :
                          'None';
        msg += `Rate Policy: ${policyText}\n`;
      }
      
      if (order.rate_policy === 'lock-today' && order.base24k_rate_ex_gst) {
        msg += `24K Rate: ₹{formatCurrency(order.base24k_rate_ex_gst)}/g\n`;
        if (order.lock_date) {
          msg += `Lock Date: ${formatDate(order.lock_date)}\n`;
        }
      }
      
      if (order.gst_applicable !== false) {
        const gstPercent = order.gst_percent_snapshot || 3;
        const gstType = order.gst_input_style === 'INCL' ? '(Inclusive)' : '(Exclusive)';
        msg += `GST: ${gstPercent}% ${gstType}\n`;
      }
      
      msg += '\n';
    }

    // Order Items
    msg += '───────────────────────────────────\n';
    msg += `ORDER ITEMS (${order.items?.length || 0})\n`;
    msg += '───────────────────────────────────\n\n';

    order.items?.forEach((item, idx) => {
      msg += `${idx + 1}. ${item.item_type || 'Item'} (${item.purity || '22K'})\n`;
      msg += '───────────────────────────────────\n';
      
      // Images
      if (item.photos && item.photos.length > 0) {
        item.photos.forEach((photo, photoIdx) => {
          msg += `📷 Image ${photoIdx + 1}: ${photo}\n`;
        });
        msg += '\n';
      }

      // Item details in 2-row format
      const weight = item.weight_type === 'Fixed' 
        ? `${item.weight_fixed}g` 
        : `${item.weight_min}-${item.weight_max}g`;
      const qty = `${item.quantity || 1} piece${parseInt(item.quantity || 1) > 1 ? 's' : ''}`;
      
      msg += `Weight: ${weight}     Qty: ${qty}\n`;
      msg += `Purity: ${item.purity || 'N/A'}`;
      if (item.size) msg += `     Size: ${item.size}`;
      msg += '\n';

      if (item.making_value) {
        const making = item.making_type === 'PERCENT' ? `${item.making_value}%` :
                      item.making_type === 'PER_GRAM' ? `₹${item.making_value}/gram` :
                      item.making_type === 'WASTAGE' ? `${item.making_value} %Wst` :
                      `₹${item.making_value}`;
        msg += `Making: ${making}\n`;
      }
      if (item.rate) msg += `Rate: ₹${item.rate}/gram\n`;
      if (item.delivery_date) msg += `Delivery: ${formatDate(item.delivery_date)}\n`;
      if (item.public_remark) msg += `Remark: ${item.public_remark}\n`;
      if (item.tag_number) msg += `Tag: ${item.tag_number}\n`;
      msg += '\n';
    });

    // Payments & Deposits
    if (order.advance_payments && order.advance_payments.length > 0) {
      msg += '───────────────────────────────────\n';
      msg += '💳 PAYMENTS & DEPOSITS\n';
      msg += '───────────────────────────────────\n\n';

      Object.keys(paymentsByMode).forEach(mode => {
        msg += `${mode.toUpperCase()}\n`;
        msg += '───────────────────────────────────\n';

        if (mode === 'Old Gold') {
          paymentsByMode[mode].forEach(payment => {
            msg += `Date: ${formatDate(payment.date)}\n`;
            msg += `Amount: ₹${formatIndianCurrency(payment.amount || 0)}\n\n`;

            if (payment.items && payment.items.length > 0) {
              msg += 'Item Details:\n';
              payment.items.forEach((item, itemIdx) => {
                msg += `• Item ${itemIdx + 1}${item.item_name ? `: ${item.item_name}` : ''}\n`;
                if (item.gross_wt) msg += `  Gross Wt: ${item.gross_wt}g\n`;
                if (item.less_wt) msg += `  Less Wt: ${item.less_wt}g\n`;
                if (item.net_wt) msg += `  Net Wt: ${item.net_wt}g\n`;
                if (item.purity) msg += `  Purity: ${item.purity}%\n`;
                if (item.fine_wt) msg += `  Fine Wt: ${item.fine_wt}g\n`;
                if (item.purchase_rate || item.rate) {
                  msg += `  Rate: ₹${formatIndianCurrency(item.purchase_rate || item.rate)}/g\n`;
                }
                if (item.line_total || item.value) {
                  msg += `  Amount: ₹${formatIndianCurrency(item.line_total || item.value)}\n`;
                }
                msg += '\n';
              });
            }
            if (payment.note) msg += `Note: ${payment.note}\n`;
          });
          msg += `Old Gold Total: ₹${formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}\n\n`;
        } else if (mode === 'Metal Credit') {
          paymentsByMode[mode]
            .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
            .forEach(payment => {
              const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
              
              msg += `Date: ${formatDate(payment.date)}\n`;
              
              if (hasDeposits) {
                // Detailed deposit breakdown
                payment.deposits.forEach((deposit, depIdx) => {
                  msg += `\nDeposit ${depIdx + 1}${deposit.item_name ? `: ${deposit.item_name}` : ''}\n`;
                  msg += '───────────────────────────────────\n';
                  
                  if (deposit.type || deposit.type_name_other) {
                    msg += `Type: ${deposit.type_name_other || deposit.type}\n`;
                  }
                  if (deposit.gross_wt) {
                    msg += `Gross Wt: ${deposit.gross_wt}g\n`;
                  }
                  if (deposit.less_wt) {
                    msg += `Less Wt: ${deposit.less_wt}g\n`;
                  }
                  if (deposit.net_wt) {
                    msg += `Net Wt: ${deposit.net_wt}g\n`;
                  }
                  if (deposit.purity || deposit.purity_label) {
                    msg += `Purity: ${deposit.purity_label || deposit.purity + '%'}\n`;
                  }
                  if (deposit.fine_wt) {
                    msg += `Fine Wt: ${deposit.fine_wt}g\n`;
                  }
                  if (deposit.reference) {
                    msg += `Ref: ${deposit.reference}\n`;
                  }
                  
                  // Add photo URLs if available
                  if (deposit.photos && deposit.photos.length > 0) {
                    msg += `Photos (${deposit.photos.length}):\n`;
                    deposit.photos.forEach((photo, photoIdx) => {
                      const photoUrl = typeof photo === 'string' ? photo : (photo.edited || photo.original);
                      if (photoUrl) {
                        msg += `📷 Photo ${photoIdx + 1}: ${photoUrl}\n`;
                      }
                    });
                  }
                  msg += '\n';
                });
              } else {
                // Fallback for old data structure
                if (payment.metal_type) msg += `Type: ${payment.metal_type}\n`;
                if (payment.gross_wt) msg += `Gross Wt: ${payment.gross_wt}g\n`;
                if (payment.net_wt) msg += `Net Wt: ${payment.net_wt}g\n`;
                if (payment.purity) msg += `Purity: ${payment.purity}%\n`;
                msg += `Fine Wt: ${payment.fine_wt || payment.fine_grams || 0}g\n`;
                if (payment.reference) msg += `Ref: ${payment.reference}\n`;
                if (payment.note) msg += `Note: ${payment.note}\n`;
                msg += '\n';
              }
            });
          
          // Calculate and display total fine weight
          const totalFineGold = paymentsByMode[mode].reduce((sum, p) => {
            if (p.deposits && p.deposits.length > 0) {
              return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
            }
            return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
          }, 0);
          
          if (totalFineGold > 0) {
            msg += `Metal Credit Total Fine Gold: ${totalFineGold.toFixed(3)}g\n\n`;
          }
        } else {
          paymentsByMode[mode].forEach(payment => {
            const amount = parseFloat(payment.amount) || 0;
            const isRefund = payment.transaction_type === 'REFUND';
            const displayAmount = isRefund ? -amount : amount;

            msg += `Date: ${formatDate(payment.date)}\n`;
            msg += `Amount: ${isRefund ? '-' : ''}₹${formatIndianCurrency(Math.abs(displayAmount))}\n`;
            if (isRefund) msg += `[REFUND]\n`;

            if (payment.mode === 'UPI' && payment.upi_ref) {
              msg += `Ref: ${payment.upi_ref}\n`;
              if (payment.upi_handle) msg += `Handle: ${payment.upi_handle}\n`;
            }
            if (payment.mode === 'Card' && payment.card_last4) {
              msg += `Card: ****${payment.card_last4}\n`;
            }
            if ((payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref) {
              msg += `UTR: ${payment.utr_ref}\n`;
            }
            if (payment.mode === 'Cheque' && payment.cheque_no) {
              msg += `Cheque: ${payment.cheque_no}\n`;
            }
            if (payment.note) msg += `Note: ${payment.note}\n`;
            msg += '\n';
          });
          msg += `${mode} Total: ₹${formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}\n\n`;
        }
      });

      // Net Advance
      msg += '═══════════════════════════════════\n';
      msg += '      NET ADVANCE PAID\n';
      msg += `      ₹${formatIndianCurrency(totals.totalAdvance)}\n`;
      msg += `   (${numberToWords(totals.totalAdvance)})\n`;
      if (totals.metalCredit > 0) {
        msg += '───────────────────────────────────\n';
        msg += '      METAL CREDIT\n';
        msg += `      ${totals.metalCredit.toFixed(2)}g fine gold\n`;
      }
      msg += '═══════════════════════════════════\n\n';
    }

    // Signatures
    msg += '✍️ SIGNATURES\n';
    msg += '───────────────────────────────────\n';
    if (order.management_signature) {
      msg += `Management: ${order.management_signature}\n`;
      if (order.management_signature_name) msg += `Name: ${order.management_signature_name}\n`;
    }
    if (order.customer_signature) {
      msg += `Customer: ${order.customer_signature}\n`;
      if (customer && customer.name) msg += `Name: ${customer.name}\n`;
    }
    msg += '\n';

    msg += 'Thank you!';

    return msg;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
        <div className="text-center py-12">
          <p className="text-gray-500">Order not found</p>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className={`min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 ${order?.has_provisional_changes ? 'print-watermark' : ''}`}>
      <style>{`
        @media print {
          .print-watermark::before {
            content: "PENDING REVIEW";
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 120px;
            font-weight: bold;
            color: rgba(255, 152, 0, 0.1);
            z-index: 1000;
            pointer-events: none;
            white-space: nowrap;
          }
        }
      `}</style>
      {/* Header with Actions - No Print - Mobile Optimized */}
      <div className="bg-white border-b-2 border-amber-200 shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Back Button and Order ID */}
          <div className="flex items-center gap-2 mb-3">
            <button 
              onClick={() => fromPage ? navigate('/admin-approvals') : navigate('/orders')} 
              className="p-2 hover:bg-gray-100 rounded-lg touch-target flex-shrink-0"
              title={fromPage ? 'Back to Pending Approvals' : 'Back to Orders'}
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate">{order.order_id}</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {order.order_date 
                  ? new Date(order.order_date).toLocaleDateString('en-IN')
                  : new Date(order.created_at).toLocaleDateString('en-IN')}
              </p>
            </div>
          </div>
          
          {/* Action Buttons - Optimized for Mobile */}
          <div className="space-y-2">
            {/* Admin Action Buttons - Show when coming from pending/provisional */}
            {fromPage && user?.role === 'Admin' && (
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold text-sm touch-target"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Approve {fromPage === 'provisional' ? 'Changes' : ''}</span>
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold text-sm touch-target"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Reject</span>
                </button>
              </div>
            )}
            
            {/* Regular Action Buttons - Grid Layout for Mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                onClick={shareOnWhatsApp}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-100 text-green-700 rounded-lg font-semibold hover:bg-green-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Share</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <Printer className="w-4 h-4" />
                <span>Print</span>
              </button>
              <button
                onClick={handleEdit}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-100 text-amber-700 rounded-lg font-semibold hover:bg-amber-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
              {(user?.role === 'Admin' || user?.role === 'Manager') && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-xs sm:text-sm touch-target"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Header - Only visible on print */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Order Summary</h1>
        <p className="text-sm text-gray-500">Order #{order.order_id}</p>
      </div>

      {/* Provisional Changes Alert - Mobile Optimized */}
      {order.has_provisional_changes && (
        <div className="max-w-6xl mx-auto px-3 sm:px-4 mt-3 sm:mt-4">
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md print:block">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg sm:text-xl">⏳</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-orange-800 mb-1">Pending Admin Review</h3>
                <p className="text-xs sm:text-sm text-orange-700 mb-2">
                  Modified by <span className="font-semibold">{order.provisional_by}</span>
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> on </span>
                  <span className="sm:hidden">on </span>
                  {new Date(order.provisional_since).toLocaleString('en-IN', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </p>
                <div className="text-xs text-orange-600 bg-orange-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded border border-orange-200 print:hidden">
                  💡 <strong>Note:</strong> Printouts show "PENDING REVIEW" until approved.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Content - Mobile Optimized */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-3 sm:space-y-4 md:space-y-6">
        
        {/* Customer & Order Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* Customer Info */}
          <div className="bg-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Customer Information</h2>
            </div>
            {customer && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold">{customer.name}</span>
                </div>
                {customer.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-semibold">{customer.phone}</span>
                  </div>
                )}
                {customer.mobile && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mobile:</span>
                    <span className="font-semibold">{customer.mobile}</span>
                  </div>
                )}
                {customer.alternate_mobile && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Alternate Mobile:</span>
                    <span className="font-semibold">{customer.alternate_mobile}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold text-xs">{customer.email}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-600">Address:</span>
                    <span className="font-semibold text-xs break-words">{customer.address}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Info */}
          <div className="bg-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Order Information</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Order Date:</span>
                <span className="font-semibold">{order.order_date || new Date(order.created_at).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created By:</span>
                <span className="font-semibold">{order.created_by}</span>
              </div>
              {order.rate_policy && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rate Policy:</span>
                    <span className="font-semibold">
                      {order.rate_policy === 'lock-today' ? 'Rate Booked Today' : 
                       order.rate_policy === 'rate-at-delivery' ? 'Rate at Delivery' : 'None'}
                    </span>
                  </div>
                  {order.rate_policy === 'lock-today' && order.base24k_rate_ex_gst && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">24K Base Rate:</span>
                        <span className="font-bold text-green-700">₹{order.base24k_rate_ex_gst.toLocaleString('en-IN')}/g</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {order.gst_applicable === false 
                          ? 'Kaccha (No GST)' 
                          : (order.gst_input_style === 'EXCL' ? 'Exclusive of GST' : 'Inclusive of 3% GST')
                        }
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Order Items ({order.items?.length || 0})</h2>
          </div>
          
          <div className="space-y-3">
            {order.items?.map((item, idx) => {
              const quantity = parseFloat(item.quantity || 1);
              const making = parseFloat(item.making_value || 0);
              
              return (
                <div key={idx} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 text-xs">
                  {/* Item Title with Badge on same line */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap flex-1">
                      <span className="font-bold text-sm text-gray-800">#{idx + 1}</span>
                      <span className="font-semibold text-sm text-gray-800">{item.item_type}</span>
                      {order.order_id && (
                        <span className="text-xs text-gray-600">({order.order_id}-{String.fromCharCode(65 + idx)})</span>
                      )}
                    </div>
                    {/* Make to Order / Ready Stock Badge - Same line as title */}
                    <span className={`px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                      item.ready_item ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {item.ready_item ? 'Ready Stock' : 'Make to Order'}
                    </span>
                  </div>

                  {/* Photos - Below title, larger size */}
                  {item.photos && item.photos.length > 0 && (
                    <div className="mb-3">
                      <div className="flex gap-2 flex-wrap">
                        {item.photos.map((img, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={img}
                            alt={`Item ${idx + 1} - Photo ${imgIdx + 1}`}
                            className="w-24 h-24 object-cover rounded border-2 border-amber-300 cursor-pointer hover:border-amber-500 transition-all hover:scale-105"
                            onClick={() => setPreviewImage(img)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Item Details */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div><span className="text-gray-600">Weight:</span> <span className="font-medium">{item.weight_type === 'Fixed' ? `${item.weight_fixed}g` : `${item.weight_min}-${item.weight_max}g`}</span></div>
                    <div><span className="text-gray-600">Purity:</span> <span className="font-medium">{item.purity}</span></div>
                    <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{quantity} pc</span></div>
                    <div><span className="text-gray-600">Making:</span> <span className="font-medium">{item.making_type === 'PERCENT' ? `${making}%` : item.making_type === 'PER_GRAM' ? `₹${making}/g` : item.making_type === 'WASTAGE' ? `${making} %Wst` : `₹${making}`}</span></div>
                    
                    {item.size && !item.ready_item && (
                      <div><span className="text-gray-600">Size:</span> <span className="font-medium">{item.size}</span></div>
                    )}
                    
                    {item.finish_color && !item.ready_item && (
                      <div><span className="text-gray-600">Finish:</span> <span className="font-medium">{item.finish_color}</span></div>
                    )}
                    
                    {item.vendor_short_code && (
                      <div className="col-span-2"><span className="text-gray-600">Vendor:</span> <span className="font-medium">{item.vendor_short_code}</span></div>
                    )}
                    
                    {item.ready_item && item.tag_number && (
                      <div className="col-span-2"><span className="text-gray-600">Tag #:</span> <span className="font-medium">{item.tag_number}</span></div>
                    )}
                    
                    {item.public_remark && (
                      <div className="col-span-2"><span className="text-gray-600">Public Remarks:</span> <span className="font-medium">{item.public_remark}</span></div>
                    )}
                    
                    {item.rate && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Rate:</span> 
                        <span className="font-semibold text-green-700"> ₹{parseFloat(item.rate).toFixed(2)}/g</span>
                        <span className="text-gray-500 ml-1">
                          ({order.tax_scope === 'KACCHA' ? 'Kaccha' : (order.gst_input_style === 'INCL' ? 'incl GST' : 'ex GST')})
                        </span>
                      </div>
                    )}
                    
                    {item.delivery_date && (
                      <div className="col-span-2 flex items-center gap-1 bg-amber-100 border border-amber-300 rounded px-2 py-1">
                        <Calendar className="w-3 h-3 text-amber-700" />
                        <span className="text-gray-700 font-medium">Delivery:</span> 
                        <span className="font-bold text-amber-700">{new Date(item.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                    )}
                    {item.ready_item && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Delivery:</span> 
                        <span className="font-medium text-green-700">Ready from Stock</span>
                      </div>
                    )}
                  </div>

                  {item.internal_remark && (
                    <div className="border-t border-amber-300 pt-2 mt-2">
                      <p className="text-gray-600 font-medium">Internal Remarks:</p>
                      <p className="font-medium text-red-700 mt-1">{item.internal_remark}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Payments & Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payments */}
          <div className="bg-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>💳</span> Payments & Deposits
            </h2>
            {order.advance_payments && order.advance_payments.length > 0 ? (
              <>
                {/* Payment Summary by Mode */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3">
                  <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                    <span>📊</span> Payment Mode Summary
                  </h3>
                  <div className="space-y-1.5 text-xs sm:text-sm">
                    {(() => {
                      // Calculate totals by payment mode
                      const modeTotals = {};
                      const paymentsByMode = {};
                      
                      order.advance_payments.forEach(payment => {
                        const mode = payment.mode || 'Other';
                        const isRefund = payment.transaction_type === 'REFUND';
                        
                        if (!modeTotals[mode]) {
                          modeTotals[mode] = {
                            cashAmount: 0,
                            fineWeight: 0,
                            count: 0
                          };
                          paymentsByMode[mode] = [];
                        }
                        modeTotals[mode].count++;
                        paymentsByMode[mode].push(payment);
                        
                        if (payment.amount) {
                          const amount = parseFloat(payment.amount);
                          // Subtract refunds, add payments
                          modeTotals[mode].cashAmount += isRefund ? -amount : amount;
                        }
                        // Fixed: Only count fine_wt (don't add fine_grams as it's the same value)
                        if (payment.fine_wt) {
                          modeTotals[mode].fineWeight += parseFloat(payment.fine_wt);
                        }
                      });
                      
                      // Sort modes for consistent display
                      const sortedModes = Object.keys(modeTotals).sort();
                      
                      return (
                        <>
                          {sortedModes.map(mode => {
                            const data = modeTotals[mode];
                            const isExpanded = expandedPaymentMode === mode;
                            
                            return (
                              <div key={mode}>
                                {/* Mode Summary Row - Clickable */}
                                <div className="flex justify-between items-center py-1.5 border-b border-green-100 last:border-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-800">{mode}</span>
                                    <button
                                      onClick={() => setExpandedPaymentMode(isExpanded ? null : mode)}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer touch-target px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                    >
                                      ({data.count} transaction{data.count > 1 ? 's' : ''})
                                    </button>
                                  </div>
                                  <div className="flex flex-col items-end">
                                    {data.cashAmount > 0 && (
                                      <span className="font-bold text-green-700">
                                        ₹{data.cashAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                    {data.fineWeight > 0 && (
                                      <span className="font-bold text-purple-700">
                                        {data.fineWeight.toFixed(3)}g
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Expanded Transaction Details for This Mode */}
                                {isExpanded && (
                                  <div className="mt-2 mb-3 ml-2 space-y-2 border-l-2 border-blue-300 pl-3 animate-slide-down">
                                    {paymentsByMode[mode].map((payment, idx) => {
                                      const isRefund = payment.transaction_type === 'REFUND';
                                      return (
                                      <div key={idx} className={`border rounded-lg p-3 shadow-sm ${isRefund ? 'bg-red-50 border-red-300' : 'bg-white border-amber-200'}`}>
                                        {/* Payment Header */}
                                        <div className="flex justify-between items-start pb-2 border-b border-gray-200">
                                          <div className="flex-1">
                                            <span className={`font-semibold text-xs ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                                              {isRefund ? '↩️ REFUND' : 'Transaction'} #{idx + 1}
                                            </span>
                                            {payment.date && (
                                              <span className="text-xs text-gray-600 block">
                                                {new Date(payment.date).toLocaleDateString('en-IN', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })}
                                              </span>
                                            )}
                                          </div>
                                          <span className={`font-bold text-sm ${isRefund ? 'text-red-700' : 'text-amber-700'}`}>
                                            {payment.amount ? `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}` : 
                                             payment.fine_wt ? `${parseFloat(payment.fine_wt).toFixed(3)}g` : '-'}
                                          </span>
                                        </div>
                                        
                                        {/* Payment Details Based on Type */}
                                        <div className="mt-2 space-y-1 text-xs">
                                          {/* UPI Payment Details */}
                                          {payment.mode === 'UPI' && (
                                            <>
                                              {payment.upi_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI Reference:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_ref}</span>
                                                </div>
                                              )}
                                              {payment.upi_id && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI ID:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_id}</span>
                                                </div>
                                              )}
                                              {payment.upi_handle && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI Handle:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_handle}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Card Payment Details */}
                                          {payment.mode === 'Card' && (
                                            <>
                                              {payment.card_last4 && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Card Last 4:</span>
                                                  <span className="font-medium text-gray-800">**** {payment.card_last4}</span>
                                                </div>
                                              )}
                                              {payment.pos_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">POS Ref:</span>
                                                  <span className="font-medium text-gray-800">{payment.pos_ref}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Bank Transfer Details */}
                                          {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && (
                                            <>
                                              {payment.utr_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UTR:</span>
                                                  <span className="font-medium text-gray-800">{payment.utr_ref}</span>
                                                </div>
                                              )}
                                              {payment.bank_name && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Bank:</span>
                                                  <span className="font-medium text-gray-800">{payment.bank_name}</span>
                                                </div>
                                              )}
                                              {payment.payer_acct_last4 && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">From A/c:</span>
                                                  <span className="font-medium text-gray-800">**** {payment.payer_acct_last4}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Cheque Details */}
                                          {payment.mode === 'Cheque' && (
                                            <>
                                              {payment.cheque_no && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Cheque No:</span>
                                                  <span className="font-medium text-gray-800">{payment.cheque_no}</span>
                                                </div>
                                              )}
                                              {payment.cheque_bank && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Bank:</span>
                                                  <span className="font-medium text-gray-800">{payment.cheque_bank}</span>
                                                </div>
                                              )}
                                              {payment.cheque_status && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Status:</span>
                                                  <span className={`font-semibold ${
                                                    payment.cheque_status === 'cleared' ? 'text-green-600' :
                                                    payment.cheque_status === 'bounced' ? 'text-red-600' :
                                                    'text-yellow-600'
                                                  }`}>
                                                    {payment.cheque_status.toUpperCase()}
                                                  </span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Old Gold Details - Enhanced with full calculations */}
                                          {payment.mode === 'Old Gold' && (
                                            <div className="bg-purple-50 border border-purple-200 rounded p-2 mt-2">
                                              <span className="text-purple-800 font-semibold block mb-2 text-xs">🪙 Old Gold Details:</span>
                                              
                                              {/* Display items if available */}
                                              {payment.items && payment.items.length > 0 ? (
                                                <div className="space-y-2">
                                                  {payment.items.map((item, itemIdx) => (
                                                    <div key={itemIdx} className="bg-white rounded p-2 border border-purple-300">
                                                      {item.item_name && (
                                                        <div className="font-medium text-xs text-purple-800 mb-1">
                                                          #{itemIdx + 1}: {item.item_name}
                                                        </div>
                                                      )}
                                                      <div className="grid grid-cols-2 gap-1 text-xs">
                                                        {item.gross_wt && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Gross:</span>
                                                            <span className="font-medium">{parseFloat(item.gross_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.less_wt && parseFloat(item.less_wt) > 0 && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Less:</span>
                                                            <span className="font-medium text-red-600">{parseFloat(item.less_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.net_wt && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Net:</span>
                                                            <span className="font-medium">{parseFloat(item.net_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.purity && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Purity:</span>
                                                            <span className="font-medium">{item.purity}%</span>
                                                          </div>
                                                        )}
                                                        {item.fine_wt && (
                                                          <div className="flex justify-between col-span-2 pt-1 border-t border-purple-200 mt-1">
                                                            <span className="text-gray-700 font-semibold">Fine:</span>
                                                            <span className="font-bold text-purple-700">{parseFloat(item.fine_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.purchase_rate && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Rate:</span>
                                                            <span className="font-medium">₹{parseFloat(item.purchase_rate).toLocaleString('en-IN')}/g</span>
                                                          </div>
                                                        )}
                                                        {item.line_total && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Total:</span>
                                                            <span className="font-bold text-green-700">₹{parseFloat(item.line_total).toLocaleString('en-IN')}</span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ))}
                                                  {/* Summary */}
                                                  <div className="bg-purple-100 rounded p-2 border border-purple-300 mt-2">
                                                    <div className="flex justify-between text-xs font-bold">
                                                      <span>Total Fine Weight:</span>
                                                      <span className="text-purple-700">{(payment.fine_grams || payment.fine_wt || 0).toFixed(3)}g</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs font-bold mt-1">
                                                      <span>Total Amount:</span>
                                                      <span className="text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                /* Fallback for old data without items */
                                                <div className="text-xs text-gray-500 italic">
                                                  {payment.fine_grams || payment.fine_wt ? (
                                                    <div>
                                                      <div className="flex justify-between mb-1">
                                                        <span>Fine Weight:</span>
                                                        <span className="font-bold text-purple-700">{(payment.fine_grams || payment.fine_wt).toFixed(3)}g</span>
                                                      </div>
                                                      <div className="flex justify-between">
                                                        <span>Amount:</span>
                                                        <span className="font-bold text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    'Details not available for this transaction'
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Metal Credit Details - Enhanced with full calculations */}
                                          {payment.mode === 'Metal Credit' && (
                                            <div className="bg-indigo-50 border border-indigo-200 rounded p-2 mt-2">
                                              <span className="text-indigo-800 font-semibold block mb-2 text-xs">💎 Metal Credit Details:</span>
                                              {payment.fine_wt && (
                                                <div className="flex justify-between text-xs pt-1 border-t border-indigo-300 mt-1 mb-2">
                                                  <span className="text-gray-700 font-semibold">Total Fine Weight:</span>
                                                  <span className="font-bold text-indigo-700">{parseFloat(payment.fine_wt).toFixed(3)}g</span>
                                                </div>
                                              )}
                                              {payment.deposits && payment.deposits.length > 0 && (
                                                <div className="space-y-2">
                                                  <span className="text-gray-700 font-semibold block text-xs">Deposit Breakdown:</span>
                                                  {payment.deposits.map((deposit, depIdx) => (
                                                    <div key={depIdx} className="bg-white rounded p-2 border border-indigo-200">
                                                      <div className="font-medium text-xs text-indigo-800 mb-1">
                                                        #{depIdx + 1}: {deposit.type_name_other || deposit.type}
                                                      </div>
                                                      {deposit.gross_wt && (
                                                        <div className="flex justify-between text-xs">
                                                          <span className="text-gray-600">Gross:</span>
                                                          <span className="font-medium">{parseFloat(deposit.gross_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {deposit.net_wt && (
                                                        <div className="flex justify-between text-xs">
                                                          <span className="text-gray-600">Net:</span>
                                                          <span className="font-medium">{parseFloat(deposit.net_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {deposit.stone_wt && parseFloat(deposit.stone_wt) > 0 && (
                                                        <div className="flex justify-between text-xs">
                                                          <span className="text-gray-600">Stone:</span>
                                                          <span className="font-medium text-red-600">{parseFloat(deposit.stone_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {deposit.purity && (
                                                        <div className="flex justify-between text-xs">
                                                          <span className="text-gray-600">Purity:</span>
                                                          <span className="font-medium">{deposit.purity}</span>
                                                        </div>
                                                      )}
                                                      {deposit.fine_wt && (
                                                        <div className="flex justify-between text-xs pt-1 border-t border-indigo-200 mt-1">
                                                          <span className="text-gray-700 font-semibold">Fine Wt:</span>
                                                          <span className="font-bold text-indigo-700">{parseFloat(deposit.fine_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {/* Show deposit photos if available */}
                                                      {deposit.photos && deposit.photos.length > 0 && (
                                                        <div className="flex gap-2 flex-wrap mt-2 pt-2 border-t border-indigo-200">
                                                          {deposit.photos.map((photo, photoIdx) => {
                                                            const displayImage = typeof photo === 'string' ? photo : photo.edited || photo.original;
                                                            return (
                                                              <img
                                                                key={photoIdx}
                                                                src={displayImage}
                                                                alt={`Deposit ${depIdx + 1} - Photo ${photoIdx + 1}`}
                                                                className="w-12 h-12 object-cover rounded border border-indigo-300 cursor-pointer hover:border-indigo-500 transition-all hover:scale-105"
                                                                onClick={() => setPreviewImage(displayImage)}
                                                              />
                                                            );
                                                          })}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Remarks/Notes for all payment types */}
                                          {(payment.note || payment.remark || payment.remarks) && (
                                            <div className="mt-2 pt-2 border-t border-gray-200 bg-yellow-50 rounded p-2">
                                              <span className="text-gray-600 font-semibold text-xs">📝 Notes/Remarks:</span>
                                              <p className="text-gray-800 text-xs mt-1">{payment.note || payment.remark || payment.remarks}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Grand Total */}
                          <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-green-300">
                            <span className="font-bold text-gray-900">Net Advance</span>
                            <div className="flex flex-col items-end">
                              {(() => {
                                const grandTotal = order.advance_payments.reduce((sum, p) => {
                                  const amount = parseFloat(p.amount) || 0;
                                  // Subtract refunds, add payments
                                  return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                                }, 0);
                                // Fixed: Only count fine_wt (don't add fine_grams as it's the same value)
                                const totalFineWeight = order.advance_payments.reduce((sum, p) => 
                                  sum + (parseFloat(p.fine_wt) || 0), 0);
                                
                                return (
                                  <>
                                    {grandTotal !== 0 && (
                                      <span className="font-bold text-green-800 text-base">
                                        ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                    {totalFineWeight > 0 && (
                                      <span className="font-bold text-purple-800">
                                        {totalFineWeight.toFixed(3)}g
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500">No payments added</p>
            )}
          </div>

          {/* Totals */}

          {/* Totals */}
          <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-300 rounded-xl p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Order Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Subtotal:</span>
                <span className="font-bold text-gray-800">₹{totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex justify-between">
                  <span className="text-gray-700">Total Advance:</span>
                  <span className="font-bold text-green-700">₹{totals.totalAdvance.toFixed(2)}</span>
                </div>
                {totals.cashAdvance > 0 && (
                  <div className="flex justify-between pl-4 text-xs">
                    <span className="text-gray-600">└─ Cash:</span>
                    <span className="font-semibold text-gray-700">₹{totals.cashAdvance.toFixed(2)}</span>
                  </div>
                )}
                {totals.metalAdvanceFineWt > 0 && (
                  <div className="flex justify-between pl-4 text-xs">
                    <span className="text-gray-600">└─ Metal Credit:</span>
                    <span className="font-semibold text-gray-700">
                      {totals.metalAdvanceFineWt.toFixed(2)}g (Approx: ₹{totals.metalAdvanceApprox.toFixed(0)})
                    </span>
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2 border-t-2 border-amber-400">
                <span className="font-bold text-gray-800">Balance Due:</span>
                <span className="font-bold text-xl text-red-700">₹{totals.balance.toFixed(2)}</span>
              </div>
              
              {/* Advance Status Message */}
              {totals.advanceStatusMessage && (
                <div className={`mt-3 p-2 rounded-lg text-xs font-semibold ${
                  totals.advanceStatus === 'sufficient'
                    ? 'bg-green-100 text-green-700 border-2 border-green-400'
                    : 'bg-yellow-100 text-yellow-700 border-2 border-yellow-400'
                }`}>
                  {totals.advanceBelowRequired && <span className="mr-1">▲</span>}
                  {totals.advanceStatusMessage}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Reminder - Show from order.reminder_date OR full reminder object */}
        {(reminder || order.reminder_date) && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span>💰</span> Payment Reminder Set
            </h2>
            {reminder ? (
              /* Full reminder object with all details */
              <div className="space-y-2 text-xs sm:text-sm">
                {/* Status */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold w-fit ${
                    reminder.status === 'resolved' ? 'bg-green-500 text-white' :
                    reminder.status === 'snoozed' ? 'bg-orange-500 text-white' :
                    reminder.status === 'overdue' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>{reminder.status.toUpperCase()}</span>
                </div>

                {/* Reminder Reason */}
                {reminder.reason_tags && reminder.reason_tags.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-gray-700 font-semibold">Reminder Reason:</span>
                    <div className="flex flex-wrap gap-1">
                      {reminder.reason_tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-200 text-blue-800 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Due Date & Time */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Due Date & Time:</span>
                  <span className="font-bold text-blue-800">
                    {new Date(reminder.due_at).toLocaleString('en-IN', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })}
                  </span>
                </div>

                {/* Target Collection (Amount to Collect) */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 bg-red-50 p-2 rounded-lg border border-red-200">
                  <span className="text-gray-700 font-semibold">🎯 Target Collection:</span>
                  <span className="font-bold text-red-700 text-base">₹{parseFloat(reminder.amount_due).toLocaleString('en-IN')}</span>
                </div>

                {/* Channels */}
                {reminder.channels && reminder.channels.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-gray-700 font-semibold">Channel(s):</span>
                    <div className="flex flex-wrap gap-1">
                      {reminder.channels.map((channel, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium border border-green-300">
                          {channel === 'whatsapp' ? '📱 WhatsApp' :
                           channel === 'sms' ? '💬 SMS' :
                           channel === 'email' ? '✉️ Email' :
                           channel === 'call' ? '📞 Call' :
                           channel === 'push' ? '🔔 App Push' : channel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auto-snooze */}
                {reminder.auto_snooze && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-gray-700 font-semibold">Auto-snooze:</span>
                    <span className="font-medium text-gray-800">
                      {reminder.auto_snooze === '+1d' ? '+1 day (Default)' :
                       reminder.auto_snooze === '+3d' ? '+3 days' :
                       reminder.auto_snooze === '+1w' ? '+1 week' : reminder.auto_snooze}
                    </span>
                  </div>
                )}

                {/* Notes */}
                {reminder.notes && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="text-gray-700 font-semibold">Notes:</span>
                    <p className="text-gray-800 mt-1 break-words">{reminder.notes}</p>
                  </div>
                )}

                {/* Message History / Follow-ups */}
                {reminder.message_history && reminder.message_history.length > 0 && (
                  <div className="mt-3 pt-3 border-t-2 border-blue-300">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-blue-700" />
                      <span className="text-gray-700 font-bold">Follow-up History ({reminder.message_history.length})</span>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {reminder.message_history.map((msg, idx) => (
                        <div key={idx} className="bg-white border border-blue-200 rounded-lg p-2">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-semibold text-blue-700">
                              {msg.channel.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(msg.sent_at).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {msg.message_text && (
                            <p className="text-xs text-gray-700 break-words">{msg.message_text}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              msg.status === 'sent' ? 'bg-green-100 text-green-700' :
                              msg.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {msg.status}
                            </span>
                            {msg.sent_by && (
                              <span className="text-xs text-gray-500">by {msg.sent_by}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Simple reminder - just date from order object */
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Reminder Date:</span>
                  <span className="font-bold text-blue-800">
                    {new Date(order.reminder_date).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 italic">
                  💡 Full reminder details will appear here once payment reminder is created via the Reminders feature.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Signatures */}
        {(order.management_signature || order.customer_signature) && (
          <div className="bg-white border-2 border-purple-200 rounded-xl p-3 sm:p-4 print:block">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">✍️ Signatures</h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {order.management_signature && (
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Management Signature</p>
                  <img 
                    src={order.management_signature} 
                    alt="Management Signature" 
                    className="w-full h-20 sm:h-24 object-contain bg-white rounded-lg border"
                  />
                  {order.management_signature_name && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {order.management_signature_name}
                    </p>
                  )}
                </div>
              )}
              {order.customer_signature && (
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Customer Signature</p>
                  <img 
                    src={order.customer_signature} 
                    alt="Customer Signature" 
                    className="w-full h-20 sm:h-24 object-contain bg-white rounded-lg border"
                  />
                  {customer && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {customer.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History - Timeline View */}
        {order.revision_history && order.revision_history.length > 0 && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-500 rounded-lg">
                <span className="text-2xl">📝</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Revision History</h2>
                <p className="text-sm text-gray-600">Track all changes made to this order</p>
              </div>
            </div>
            <div className="space-y-0">
              {order.revision_history.map((revision, idx) => (
                <RevisionTimelineCard
                  key={idx}
                  revision={revision}
                  revisionNumber={revision.revision_number || (order.revision_history.length - idx)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Order?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete order <strong>{order.order_id}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Reject Order</h2>
            <p className="text-gray-600 mb-4">Please provide a reason for rejecting this order:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none mb-4"
              rows="4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
              >
                Reject Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Format Selector Modal */}
      <PrintFormatSelector 
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onSelectFormat={handlePrintFormat}
        hasItemReceipts={getOldGoldAndMetalCreditItems().length > 0}
      />

      {/* A4 Print Content (Hidden, only visible when printing) */}
      <div id="a4-print-content" className="hidden">
        {/* Watermark for provisional orders */}
        {order.has_provisional_changes && (
          <div className="print-watermark">PENDING REVIEW</div>
        )}

        {/* Page Content */}
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4', color: '#000' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 5px 0' }}>ORDER FORM</h1>
            <p style={{ margin: 0, fontSize: '10px', color: '#666' }}>Order ID: {order.order_id} | Date: {new Date(order.order_date || order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
          </div>

          {/* Customer Information */}
          <div style={{ marginBottom: '15px', border: '1px solid #000', padding: '8px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 'bold', margin: '0 0 5px 0', borderBottom: '1px solid #ccc', paddingBottom: '3px' }}>CUSTOMER INFORMATION</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', fontSize: '10px' }}>
              <div><strong>Name:</strong> {customer?.name || 'N/A'}</div>
              <div><strong>Mobile:</strong> {customer?.mobile || 'N/A'}</div>
              {customer?.email && <div><strong>Email:</strong> {customer.email}</div>}
              {customer?.address && <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong> {customer.address}</div>}
            </div>
          </div>

          {/* Order Items */}
          <div style={{ marginBottom: '15px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 'bold', margin: '0 0 8px 0', borderBottom: '1px solid #000', paddingBottom: '3px' }}>ORDER ITEMS ({order.items?.length || 0})</h3>
            {order.items?.map((item, index) => (
              <div key={index} style={{ marginBottom: '12px', border: '1px solid #ddd', padding: '6px', pageBreakInside: 'avoid', display: 'flex', gap: '8px' }}>
                {/* Item Image */}
                {item.photo_url && (
                  <img 
                    src={item.photo_url} 
                    alt={item.name}
                    style={{ width: '80px', height: '80px', objectFit: 'cover', border: '1px solid #ccc', flexShrink: 0 }}
                  />
                )}
                
                {/* Item Details */}
                <div style={{ flex: 1, fontSize: '9px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '3px' }}>{index + 1}. {item.name || 'Item'} - {item.purity || '22K'}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px 5px' }}>
                    <div><strong>Category:</strong> {item.category || 'N/A'}</div>
                    <div><strong>Gross Wt:</strong> {parseFloat(item.gross_wt || 0).toFixed(3)}g</div>
                    <div><strong>Net Wt:</strong> {parseFloat(item.net_wt || 0).toFixed(3)}g</div>
                    <div><strong>Fine Wt:</strong> {parseFloat(item.fine_wt || 0).toFixed(3)}g</div>
                    <div><strong>Making:</strong> ₹{parseFloat(item.making || 0).toLocaleString('en-IN')}</div>
                    <div><strong>Stone:</strong> ₹{parseFloat(item.stone_value || 0).toLocaleString('en-IN')}</div>
                    {item.size && <div><strong>Size:</strong> {item.size}</div>}
                    {item.quantity > 1 && <div><strong>Qty:</strong> {item.quantity}</div>}
                  </div>
                  {item.customer_note && (
                    <div style={{ marginTop: '3px', fontStyle: 'italic', color: '#555' }}>
                      <strong>Note:</strong> {item.customer_note}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Rate Information */}
          <div style={{ marginBottom: '15px', border: '1px solid #000', padding: '6px', fontSize: '10px' }}>
            <h3 style={{ fontSize: '11px', fontWeight: 'bold', margin: '0 0 4px 0' }}>RATE INFORMATION</h3>
            {order.base24k_rate_ex_gst && (
              <div><strong>24K Base Rate:</strong> ₹{parseFloat(order.base24k_rate_ex_gst).toLocaleString('en-IN')}/g {
                order.tax_scope === 'KACCHA' ? '(Kaccha)' : '(ex. GST)'
              }</div>
            )}
            <div><strong>Rate Policy:</strong> {
              order.rate_policy === 'rate-at-delivery' ? 'Rate at Delivery' :
              order.rate_policy === 'lock-today' ? 'Rate Locked Today' : 'Standard Rate'
            }</div>
          </div>

          {/* Payments & Deposits */}
          <div style={{ marginBottom: '15px', border: '1px solid #000', padding: '6px' }}>
            <h3 style={{ fontSize: '11px', fontWeight: 'bold', margin: '0 0 5px 0', borderBottom: '1px solid #ccc', paddingBottom: '2px' }}>PAYMENTS & DEPOSITS</h3>
            
            {/* Payment Summary */}
            {order.advance_payments && order.advance_payments.length > 0 && (
              <>
                <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>Payment Summary:</div>
                <div style={{ fontSize: '9px', marginBottom: '6px', paddingLeft: '10px' }}>
                  {(() => {
                    const payments = order.advance_payments.filter(p => p.transaction_type !== 'REFUND');
                    const refunds = order.advance_payments.filter(p => p.transaction_type === 'REFUND');
                    
                    const paymentModeTotals = {};
                    payments.forEach(payment => {
                      const mode = payment.mode || 'Other';
                      if (!paymentModeTotals[mode]) {
                        paymentModeTotals[mode] = { cash: 0, weight: 0, count: 0 };
                      }
                      paymentModeTotals[mode].count++;
                      if (payment.amount) paymentModeTotals[mode].cash += parseFloat(payment.amount);
                      if (payment.fine_wt) paymentModeTotals[mode].weight += parseFloat(payment.fine_wt);
                      if (payment.fine_grams) paymentModeTotals[mode].weight += parseFloat(payment.fine_grams);
                    });
                    
                    const refundModeTotals = {};
                    refunds.forEach(refund => {
                      const mode = refund.mode || 'Other';
                      if (!refundModeTotals[mode]) {
                        refundModeTotals[mode] = { cash: 0, count: 0 };
                      }
                      refundModeTotals[mode].count++;
                      if (refund.amount) refundModeTotals[mode].cash += parseFloat(refund.amount);
                    });
                    
                    return (
                      <>
                        {/* Payments Received */}
                        <div style={{ marginBottom: '4px' }}>
                          <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#059669', marginBottom: '2px' }}>+ Payments Received:</div>
                          {Object.keys(paymentModeTotals).sort().map(mode => (
                            <div key={mode} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', paddingLeft: '8px' }}>
                              <span>• {mode} ({paymentModeTotals[mode].count})</span>
                              <span style={{ fontWeight: 'bold' }}>
                                {paymentModeTotals[mode].cash > 0 && `₹${paymentModeTotals[mode].cash.toLocaleString('en-IN')}`}
                                {paymentModeTotals[mode].weight > 0 && ` ${paymentModeTotals[mode].weight.toFixed(3)}g`}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Refunds Given */}
                        {refunds.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#dc2626', marginBottom: '2px' }}>- Refunds Given:</div>
                            {Object.keys(refundModeTotals).sort().map(mode => (
                              <div key={mode} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', paddingLeft: '8px' }}>
                                <span>• {mode} ({refundModeTotals[mode].count})</span>
                                <span style={{ fontWeight: 'bold', color: '#dc2626' }}>
                                  -₹{refundModeTotals[mode].cash.toLocaleString('en-IN')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div style={{ borderTop: '1px solid #000', marginTop: '3px', paddingTop: '3px', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Net Advance</span>
                      <span>
                        {(() => {
                          const payments = order.advance_payments.filter(p => p.transaction_type !== 'REFUND');
                          const refunds = order.advance_payments.filter(p => p.transaction_type === 'REFUND');
                          const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                          const totalRefunds = refunds.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                          const netAdvance = totalPayments - totalRefunds;
                          // Fixed: Only count fine_wt (don't add fine_grams as it's the same value)
                          const totalWeight = payments.reduce((sum, p) => sum + (parseFloat(p.fine_wt) || 0), 0);
                          return `₹${netAdvance.toLocaleString('en-IN')}${totalWeight > 0 ? ` | ${totalWeight.toFixed(3)}g` : ''}`;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Transaction Details */}
                <div style={{ fontSize: '10px', fontWeight: 'bold', marginTop: '8px', marginBottom: '4px' }}>Transaction Details:</div>
                {order.advance_payments.map((payment, idx) => {
                  const isRefund = payment.transaction_type === 'REFUND';
                  return (
                  <div key={idx} style={{ fontSize: '9px', marginBottom: '6px', paddingLeft: '10px', borderLeft: `2px solid ${isRefund ? '#dc2626' : '#059669'}`, paddingTop: '3px', paddingBottom: '3px', backgroundColor: isRefund ? '#fef2f2' : '#f0fdf4' }}>
                    <div style={{ fontWeight: 'bold', color: isRefund ? '#dc2626' : '#059669' }}>
                      {isRefund ? '↩️ REFUND' : '💰 PAYMENT'} - {payment.mode} - {payment.amount ? `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}` : `${parseFloat(payment.fine_wt || 0).toFixed(3)}g`}
                    </div>
                    {payment.date && <div>Date: {new Date(payment.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>}
                    {isRefund && payment.reason && <div>Reason: {payment.reason}</div>}
                    {payment.upi_ref && <div>UPI Ref: {payment.upi_ref}</div>}
                    {payment.upi_id && <div>UPI ID: {payment.upi_id}</div>}
                    {payment.upi_handle && <div>UPI Handle: {payment.upi_handle}</div>}
                    {payment.card_last4 && <div>Card: **** {payment.card_last4}</div>}
                    {payment.pos_ref && <div>POS Ref: {payment.pos_ref}</div>}
                    {payment.utr_ref && <div>UTR: {payment.utr_ref}</div>}
                    {payment.bank_name && <div>Bank: {payment.bank_name}</div>}
                    {payment.payer_acct_last4 && <div>From A/c: **** {payment.payer_acct_last4}</div>}
                    {payment.cheque_no && <div>Cheque No: {payment.cheque_no}</div>}
                    {payment.cheque_bank && <div>Bank: {payment.cheque_bank}</div>}
                    {payment.cheque_status && <div>Status: {payment.cheque_status.toUpperCase()}</div>}
                    {payment.fine_grams && <div>Fine Weight: {parseFloat(payment.fine_grams).toFixed(3)}g</div>}
                    {payment.note && <div style={{ fontStyle: 'italic', marginTop: '2px' }}>Note: {payment.note}</div>}
                  </div>
                );
                })}
              </>
            )}
          </div>

          {/* Totals */}
          <div style={{ border: '2px solid #000', padding: '8px', marginBottom: '20px', fontSize: '11px', fontWeight: 'bold' }}>
            <h3 style={{ fontSize: '12px', margin: '0 0 5px 0' }}>ORDER SUMMARY</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
              {(() => {
                const totals = calculateTotals();
                return (
                  <>
                    <div>Total Gross Weight: {totals.totalWeight.toFixed(3)}g</div>
                    <div>Total Fine Weight: {totals.totalFineWeight.toFixed(3)}g</div>
                    <div>Total Making: ₹{totals.totalMaking.toLocaleString('en-IN')}</div>
                    <div>Total Stone: ₹{totals.totalStone.toLocaleString('en-IN')}</div>
                    <div>Total Advance (Cash): ₹{totals.totalAdvanceCash.toLocaleString('en-IN')}</div>
                    <div>Metal Credit Used: {totals.totalAdvanceMetal.toFixed(3)}g</div>
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #000', paddingTop: '5px', marginTop: '5px', fontSize: '13px' }}>
                      Balance Due: ₹{totals.balance.toLocaleString('en-IN')}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Signature */}
          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderBottom: '1px solid #000', width: '200px', marginBottom: '5px' }}>&nbsp;</div>
              <div style={{ fontSize: '10px' }}>Customer Signature</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderBottom: '1px solid #000', width: '200px', marginBottom: '5px' }}>&nbsp;</div>
              <div style={{ fontSize: '10px' }}>Authorized Signature</div>
            </div>
          </div>
        </div>
      </div>

      {/* Thermal Print Content (Hidden, 58mm width for 3-inch printer) */}
      <div id="thermal-print-content" className="hidden">
        <div style={{ width: '58mm', fontFamily: 'Courier New, monospace', fontSize: '10px', lineHeight: '1.3' }}>
          <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '5px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>ORDER RECEIPT</div>
            <div style={{ fontSize: '9px' }}>Order ID: {order.order_id}</div>
            <div style={{ fontSize: '9px' }}>{new Date(order.order_date || order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>

          {/* Customer */}
          <div style={{ borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '5px' }}>
            <div style={{ fontSize: '9px', fontWeight: 'bold' }}>CUSTOMER:</div>
            <div style={{ fontSize: '9px' }}>{customer?.name || 'N/A'}</div>
            <div style={{ fontSize: '9px' }}>{customer?.mobile || 'N/A'}</div>
          </div>

          {/* Items */}
          <div style={{ borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '5px' }}>
            <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>ITEMS:</div>
            {order.items?.map((item, index) => (
              <div key={index} style={{ fontSize: '8px', marginBottom: '4px' }}>
                <div style={{ fontWeight: 'bold' }}>{index + 1}. {item.name} - {item.purity}</div>
                <div>Gross: {parseFloat(item.gross_wt || 0).toFixed(2)}g Net: {parseFloat(item.net_wt || 0).toFixed(2)}g</div>
                <div>Fine: {parseFloat(item.fine_wt || 0).toFixed(3)}g</div>
                <div>Making: Rs.{parseFloat(item.making || 0).toLocaleString('en-IN')}</div>
                {item.stone_value > 0 && <div>Stone: Rs.{parseFloat(item.stone_value).toLocaleString('en-IN')}</div>}
              </div>
            ))}
          </div>

          {/* Payments */}
          <div style={{ borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '5px' }}>
            <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>PAYMENTS:</div>
            {order.advance_payments && order.advance_payments.length > 0 && (
              <>
                {(() => {
                  const payments = order.advance_payments.filter(p => p.transaction_type !== 'REFUND');
                  const refunds = order.advance_payments.filter(p => p.transaction_type === 'REFUND');
                  
                  const paymentModeTotals = {};
                  payments.forEach(payment => {
                    const mode = payment.mode || 'Other';
                    if (!paymentModeTotals[mode]) paymentModeTotals[mode] = { cash: 0, weight: 0 };
                    if (payment.amount) paymentModeTotals[mode].cash += parseFloat(payment.amount);
                    if (payment.fine_wt) paymentModeTotals[mode].weight += parseFloat(payment.fine_wt);
                    if (payment.fine_grams) paymentModeTotals[mode].weight += parseFloat(payment.fine_grams);
                  });
                  
                  const refundModeTotals = {};
                  refunds.forEach(refund => {
                    const mode = refund.mode || 'Other';
                    if (!refundModeTotals[mode]) refundModeTotals[mode] = { cash: 0 };
                    if (refund.amount) refundModeTotals[mode].cash += parseFloat(refund.amount);
                  });
                  
                  const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                  const totalRefunds = refunds.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                  const netAdvance = totalPayments - totalRefunds;
                  
                  return (
                    <>
                      {Object.keys(paymentModeTotals).sort().map(mode => (
                        <div key={mode} style={{ fontSize: '8px', display: 'flex', justifyContent: 'space-between' }}>
                          <span>+ {mode}</span>
                          <span>
                            {paymentModeTotals[mode].cash > 0 && `Rs.${paymentModeTotals[mode].cash.toLocaleString('en-IN')}`}
                            {paymentModeTotals[mode].weight > 0 && ` ${paymentModeTotals[mode].weight.toFixed(2)}g`}
                          </span>
                        </div>
                      ))}
                      {refunds.length > 0 && Object.keys(refundModeTotals).sort().map(mode => (
                        <div key={`ref-${mode}`} style={{ fontSize: '8px', display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                          <span>- {mode} Refund</span>
                          <span>-Rs.{refundModeTotals[mode].cash.toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                      <div style={{ borderTop: '1px solid #000', marginTop: '2px', paddingTop: '2px', fontSize: '9px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Net Advance</span>
                        <span>Rs.{netAdvance.toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {/* Summary */}
          <div style={{ borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '5px' }}>
            <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>SUMMARY:</div>
            {(() => {
              const totals = calculateTotals();
              return (
                <div style={{ fontSize: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Wt:</span><span>{totals.totalWeight.toFixed(2)}g</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Fine Wt:</span><span>{totals.totalFineWeight.toFixed(3)}g</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Advance:</span><span>Rs.{totals.totalAdvanceCash.toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '2px', paddingTop: '2px', borderTop: '1px solid #000' }}>
                    <span>Balance:</span><span>Rs.{totals.balance.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Signature */}
          <div style={{ marginTop: '8px', marginBottom: '5px' }}>
            <div style={{ fontSize: '8px', textAlign: 'center' }}>Customer Signature:</div>
            <div style={{ borderBottom: '1px solid #000', margin: '3px 10px' }}>&nbsp;</div>
          </div>

          <div style={{ textAlign: 'center', fontSize: '9px', marginTop: '5px' }}>
            <div>Thank You!</div>
            <div>Visit Again</div>
          </div>
        </div>
      </div>

      {/* Print Format Components (Hidden from screen, only for printing) */}
      <div className="print-formats-container">
        {/* A4 Print Component */}
        <OrderPrintA4 order={order} customer={customer} showItemRate={showItemRate} />
        
        {/* Thermal Print Component - Detailed */}
        <OrderPrintThermal order={order} customer={customer} showItemRate={showItemRate} />
        
        {/* Thermal Print Component - Summary */}
        <OrderPrintThermalSummary order={order} customer={customer} showItemRate={showItemRate} />
      </div>

      {/* Item Receipt Selector Modal */}
      <ItemReceiptSelector
        isOpen={showItemReceiptSelector}
        onClose={() => setShowItemReceiptSelector(false)}
        items={getOldGoldAndMetalCreditItems()}
        onPrintSelected={handlePrintSelectedItems}
      />

      {/* Item Receipt Print Components (Hidden from screen, only for printing) */}
      <div className="item-receipts-container">
        {getOldGoldAndMetalCreditItems().map((item, index) => (
          <ItemReceiptPrint
            key={index}
            item={item}
            itemIndex={index}
            customer={customer}
            moduleRef={`Order: ${order?.order_id}`}
            transactionDate={item.payment_date || order?.created_at}
            transactionId={item.payment_id}
          />
        ))}
      </div>
    </div>
  );
};

export default OrderDetailNew;
